// src/pages/vsession.jsx
import React, { useEffect, useRef, useState } from "react";
import "../../../styles/VideoSession.css";
import { FaMicrophoneSlash, FaVideoSlash } from "react-icons/fa";
import AiModelIcon from "../../../assets/icons/aimodel.svg";
import VoiceModelIcon from "../../../assets/icons/voicemodel.svg";
import EndCallIcon from "../../../assets/icons/endcall.svg";
import { ReactComponent as VoiceAudioIcon } from "../../../assets/icons/audiomic.svg";
import { ReactComponent as VideoIcon } from "../../../assets/icons/video.svg";
import { useWebSocketChat } from "../../../socket/useSocket";
import { AuthServices } from "../../auth/services/AuthServices";
import dietician from "../../../assets/images/dietician.png";
import psychologist from "../../../assets/images/psychologist.png";
import { useNavigate } from "react-router-dom";

const VideoSession = () => {
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [showModelModal, setShowModelModal] = useState(false);
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [showEndModal, setShowEndModal] = useState(false);
  const [selectedModelType, setSelectedModelType] = useState("psychologist");
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [user, setUser] = useState();
  const [selectedExpert, setSelectedExpert] = useState(() => {
    return localStorage.getItem("selectedExpert") || "";
  });

  const {
    socket,
    updatePersona,
    updatePatientId,
    sendAudio,
    audioChunksRef,
    setBotStatus,
    botStatus,
  } = useWebSocketChat({
    setCompleteAudio: () => { },
    onStreamingStart: (data) => handleStreamingStart(data),
    onStreamingText: () => { },
    onStreamingAudio: (data) => handleStreamAudio(data),
    onStreamingComplete: (data) => handleStreamingComplete(data),
    onStreamingError: (data) => handleStreamError(data),
    onTranscription: () => { },
    onConversationHistory: () => { },
  });

  const userVideoRef = useRef(null);
  const audioRef = useRef(new Audio());
  const audioQueue = useRef([]);
  const isProcessingQueue = useRef(false);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const isRecordingRef = useRef(false);
  const navigate = useNavigate();

  const handleVideoToggle = () => {
    setIsVideoOff((prev) => {
      const newVideoOff = !prev;

      const stream = userVideoRef.current?.srcObject;
      if (stream) {
        const videoTracks = stream.getVideoTracks();

        if (newVideoOff) {
          videoTracks.forEach((track) => {
            track.stop();
            stream.removeTrack(track);
          });
        } else {
          navigator.mediaDevices
            .getUserMedia({ video: true })
            .then((newStream) => {
              const newVideoTrack = newStream.getVideoTracks()[0];
              stream.addTrack(newVideoTrack);
              userVideoRef.current.srcObject = stream;
            })
            .catch((err) => {
              console.error("Failed to restart camera:", err);
            });
        }
      }

      return newVideoOff;
    });
  };

  const handleMuteToggle = () => {
    setIsMuted((prev) => {
      const newMuted = !prev;
      const audioTracks = userVideoRef.current?.srcObject?.getAudioTracks();
      if (audioTracks && audioTracks.length > 0) {
        audioTracks[0].enabled = !newMuted;
      }
      return newMuted;
    });
  };

  const handleModelSelect = (type) => {
    setSelectedModelType(type.target.innerText);
  };

  const handleStreamingStart = (data) => {
    console.log(data);
  };

  const handleStreamAudio = (data) => {
    if (!data || !data.audio) return;
    enqueueAudio(data.audio);
  };

  const handleSetVoice = (voice) => {
    setSelectedVoice(voice.target.innerText);
  };

  const handleVoiceSelect = () => {
    let mappedVoice;

    switch (selectedVoice) {
      case "Nova – Female, Soft":
        mappedVoice = "nova";
        break;
      case "Alloy – Neutral":
        mappedVoice = "alloy";
        break;
      case "Echo – Male":
        mappedVoice = "echo";
        break;
      case "Fable – Female":
        mappedVoice = "fable";
        break;
      case "Onyx – Male, Deep":
        mappedVoice = "onyx";
        break;
      case "Shimmer – Female, Clear":
        mappedVoice = "shimmer";
        break;
      default:
        mappedVoice = "default";
    }

    localStorage.setItem("selectedVoice", mappedVoice);
    window.location.reload();

    setShowVoiceModal(false);
  };

  const enqueueAudio = (audioData) => {
    const blob = new Blob(
      [Uint8Array.from(atob(audioData), (c) => c.charCodeAt(0))],
      {
        type: "audio/mpeg",
      }
    );
    audioQueue.current.push(blob);
    if (!isProcessingQueue.current) processAudioQueue();
  };

  const processAudioQueue = async () => {
    const audio = audioRef.current;
    if (!audio) {
      console.warn("Audio element not ready yet");
      return;
    }

    if (audioQueue.current.length === 0) {
      isProcessingQueue.current = false;
      return;
    }

    isProcessingQueue.current = true;
    const blob = audioQueue.current.shift();
    const url = URL.createObjectURL(blob);

    try {
      audio.src = url;
      await audio.play();

      audio.onended = () => {
        URL.revokeObjectURL(url);
        processAudioQueue();
      };
    } catch (err) {
      console.error("Audio playback error:", err);
      URL.revokeObjectURL(url);
      isProcessingQueue.current = false;
    }
  };

  const startRecording = async () => {
    if (!audioRef.current?.paused) {
      console.log("Bot audio is still playing. Waiting to start recording...");
      setTimeout(() => {
        startRecording();
      }, 500);
      return;
    }
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      alert("Audio recording is not supported in this browser.");
      return;
    }

    setBotStatus("listening");
    console.log("Recording started...");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm",
      });

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        console.log("MediaRecorder stopped. Sending audio...");
        await sendAudio();
        isRecordingRef.current = false;
        stream.getTracks().forEach((track) => track.stop());
        mediaRecorderRef.current = null;
      };

      mediaRecorder.start();
      isRecordingRef.current = true;

      detectSilence(stream, 2000, 0.01, () => {
        stopRecording();
        isRecordingRef.current = false;
      });
    } catch (error) {
      console.error("Microphone error:", error);
      alert(`Microphone error: ${error.message}`);
    }
  };

  const detectSilence = (
    stream,
    silenceDuration = 2000,
    threshold = 0.01,
    onSilence = () => { }
  ) => {
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const analyser = audioCtx.createAnalyser();
    const source = audioCtx.createMediaStreamSource(stream);
    const data = new Uint8Array(analyser.fftSize);

    source.connect(analyser);

    let silenceStart = null;
    let startedTalking = false;

    const checkSilence = () => {
      analyser.getByteTimeDomainData(data);

      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const val = (data[i] - 128) / 128;
        sum += val * val;
      }
      const volume = Math.sqrt(sum / data.length);

      if (volume > threshold) {
        startedTalking = true;
        silenceStart = null;
      }

      if (startedTalking) {
        if (volume < threshold) {
          if (!silenceStart) silenceStart = performance.now();
          if (performance.now() - silenceStart > silenceDuration) {
            onSilence();
            audioCtx.close();
            return;
          }
        } else {
          silenceStart = null;
        }
      }

      requestAnimationFrame(checkSilence);
    };

    checkSilence();
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecordingRef.current) {
      mediaRecorderRef.current.stop();
      isRecordingRef.current = false;
    }
  };

  const handleStreamingComplete = () => {
    console.log("Bot finished responding.");
    setBotStatus(null);
    startRecording();
  };

  const handleStreamError = (data) => {
    console.log(data);
  };

  const fetchUserData = async () => {
    try {
      const response = await AuthServices.fetchUserData();
      const fetchedUser = response?.data?.data?.user;

      if (response.status !== 200) {
        socket?.close();
        localStorage.clear();
        navigate("/");
      }

      if (fetchedUser) {
        setUser(fetchedUser);
        localStorage.setItem("user", JSON.stringify(fetchedUser));

        if (fetchedUser?.patient_id) {
          updatePatientId(fetchedUser.patient_id);
        }
      }
    } catch (error) {
      console.error("Failed to fetch user data:", error);
    }
  };

  const handleSendSelectedModel = () => {
    setSelectedExpert(selectedModelType);
    localStorage.setItem("selectedExpert", selectedModelType);
    updatePersona(selectedModelType);
    setShowModelModal(false);

    if (socket && socket.readyState === 1) {
      socket.close();
    }

    window.location.reload();
  };

  useEffect(() => {
    const initCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
        if (userVideoRef.current) {
          userVideoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error("Error accessing camera:", err);
      }
    };

    initCamera();
  }, []);

  useEffect(() => {
    if (socket?.readyState === 1) {
      const initRecording = async () => {
        await startRecording();
      };
      initRecording();
    }
  }, [socket]);

  useEffect(() => {
    if (audioRef.current) {
      processAudioQueue();
    }
  }, []);

  useEffect(() => {
    const getUser = async () => {
      fetchUserData();
    };
    getUser();
  }, []);

  useEffect(() => {
    if (user?.patient_id) {
      updatePersona(selectedExpert);
    }
  }, [user]);

  return (
    <div className="video-session-container">
      <div className="status-bar">
        <p>Status: {socket?.readyState === 1 ? "Connected" : "Connecting"}</p>
        {botStatus && (
          <p>
            {botStatus === "thinking"
              ? "Thinking..."
              : botStatus === "listening"
                ? "Listening..."
                : botStatus}
          </p>
        )}
      </div>

      <div className="video-block top">
        <img
          src={selectedExpert !== "psychologist" ? dietician : psychologist}
          alt="Psychologist AI Model"
          className="video-feed"
        />
      </div>

      <div className="video-block bottom">
        <video ref={userVideoRef} autoPlay muted className="video-feed" />
      </div>

      <footer className="session-controls">
        <button className="circle-btn" onClick={handleMuteToggle}>
          {isMuted ? (
            <FaMicrophoneSlash color="#f00" size={20} />
          ) : (
            <VoiceAudioIcon width={20} height={20} />
          )}
        </button>

        <button className="circle-btn" onClick={handleVideoToggle}>
          {isVideoOff ? (
            <FaVideoSlash color="#f00" size={20} />
          ) : (
            <VideoIcon width={20} height={20} />
          )}
        </button>

        <button className="circle-btn" onClick={() => setShowModelModal(true)}>
          <img src={AiModelIcon} alt="AI Model" />
        </button>

        <button className="circle-btn" onClick={() => setShowVoiceModal(true)}>
          <img src={VoiceModelIcon} alt="Voice Model" />
        </button>

        <div className="divider" />

        <button
          className="circle-btn end-call"
          onClick={() => setShowEndModal(true)}
        >
          <img src={EndCallIcon} alt="End Call" />
        </button>
      </footer>

      {/* Model Selection Modal */}
      {showModelModal && (
        <div className="voice-modal-overlay">
          <div className="voice-modal">
            <div className="modal-header-indicator" />
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                marginBottom: "24px",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Choose Your AI Model
            </h3>
            <ul className="voice-list">
              {["psychologist", "dietician"].map((model, index) => (
                <li
                  style={{
                    backgroundColor:
                      model === selectedModelType ? "#dfefff" : "",
                    cursor: "pointer",
                    borderRadius: "8px",
                    padding: "10px",
                    transition: "0.2s",
                  }}
                  key={index}
                  className="voice-item"
                  onClick={handleModelSelect}
                >
                  <span>{model}</span>
                </li>
              ))}
            </ul>
            <div
              className="modal-actions"
              style={{ borderTop: "1px solid #eee", paddingTop: "12px" }}
            >
              <button
                className="cancel"
                onClick={() => setShowModelModal(false)}
                style={{
                  background: "transparent",
                  border: "1px solid #ccc",
                  borderRadius: "12px",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#6b7280",
                }}
              >
                Cancel
              </button>
              <button
                className="done"
                disabled={!selectedModelType}
                onClick={() => handleSendSelectedModel()}
                style={{
                  backgroundColor: "#7d6bea",
                  color: "white",
                  borderRadius: "12px",
                  fontSize: "14px",
                  fontWeight: "600",
                  opacity: selectedModelType ? 1 : 0.5,
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Voice Selection Modal */}
      {showVoiceModal && (
        <div className="voice-modal-overlay">
          <div className="voice-modal">
            <div className="modal-header-indicator" />
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                marginBottom: "20px",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Choose Your AI Model Voice
            </h3>
            <ul className="voice-list" style={{ marginBottom: "24px" }}>
              {[
                "Nova – Female, Soft",
                "Alloy – Neutral",
                "Echo – Male",
                "Fable – Female",
                "Onyx – Male, Deep",
                "Shimmer – Female, Clear",
              ].map((voice, index) => (
                <li
                  key={index}
                  className="voice-item"
                  onClick={(index) => handleSetVoice(index)}
                  style={{
                    justifyContent: "space-between",
                    padding: "10px 14px",
                    borderRadius: "12px",
                    backgroundColor: "#fff",
                    border: "1px solid #eee",
                    fontSize: "14px",
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  <span>{voice}</span>
                  <input
                    type="radio"
                    checked={selectedVoice === voice}
                    onChange={() => setSelectedVoice(voice)}
                  />
                </li>
              ))}
            </ul>
            <div
              className="modal-actions"
              style={{ borderTop: "1px solid #eee", paddingTop: "12px" }}
            >
              <button
                className="cancel"
                onClick={() => setShowVoiceModal(false)}
                style={{
                  background: "transparent",
                  border: "1px solid #ccc",
                  borderRadius: "12px",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#6b7280",
                }}
              >
                Cancel
              </button>
              <button
                className="done"
                disabled={!selectedVoice}
                onClick={handleVoiceSelect}
                style={{
                  backgroundColor: "#7d6bea",
                  color: "white",
                  borderRadius: "12px",
                  fontSize: "14px",
                  fontWeight: "600",
                  opacity: selectedVoice ? 1 : 0.5,
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* End Call Modal */}
      {showEndModal && (
        <div className="voice-modal-overlay">
          <div className="voice-modal">
            <h3 style={{ color: "#7D4AEA", fontWeight: 600 }}>
              Are you sure you want to end this call?
            </h3>
            <div className="modal-actions">
              <button className="cancel" onClick={() => setShowEndModal(false)}>
                Stay on call
              </button>
              <button
                className="done"
                onClick={() => {
                  window.location.href = "/home";
                }}
                style={{
                  background: "#ff4d4f",
                  color: "white",
                  border: "none",
                }}
              >
                End Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoSession;
